/**
 * What feeds a compute, declared once. M4S-5595 item 4.
 *
 * Three hand-maintained lists used to disagree about this: the `computed` memo's dependency array,
 * `isDirty`'s inline `computeKeys`, and the persist payloads. `statTest` and `perQuestionT2B2` were
 * read by the compute options and absent from `computeKeys`, so changing either was a no-op that
 * ALSO left Run disabled — a change that could not reach the table and could not be forced through.
 *
 * The fix is structural, not another list. `buildComputeOpts` is the single place the options
 * object is built, and `isComputeDirty` runs it over the live state and the committed state, each
 * with its own inputs (M4S-5777 X34: filters, the weight scheme and row overrides wait for Run, so
 * each side carries them in `ComputeState.inputs`). Survey names are the one live-by-design input
 * left; both sides get none, so they cancel by construction, and no future `settings.X` read can
 * escape the dirty check by being forgotten in a list.
 *
 * `COMPUTE_SETTING_KEYS` and `LIVE_COMPUTE_INPUTS` below are the enumeration the definition of
 * done asks for. They are asserted by compute-inputs.test.ts. `COMPUTE_SETTING_KEYS` is also what
 * Cancel restores from the last Run (`discardToRun`, src/lib/crosstab/pending-run.ts).
 * `Settings` carries an index signature, so `(keyof Settings)[]` gives no compile-time protection
 * whatsoever — the exhaustiveness test walks `DEFAULT_SETTINGS` instead of a type, which is why it
 * catches a newly added setting that quietly starts feeding compute.
 */

import type { ComputeOpts } from './data';
import type { FilterTabs, LiveFilter } from './crosstab-types';
import type { WeightTarget } from './raking';
import type { MatrixEditsMap } from '@/lib/crosstab/matrix-edits';
import type { AnswerEditsMap } from '@/lib/crosstab/answer-edits';
import type { BoxChoiceMap } from '@/lib/crosstab/box-rows';
import type { CrosstabQuestionOptionDto } from '@/lib/crosstab/question-formatter';
import type { SigCorrection } from '@/lib/crosstab/sig-correction';
import { DEFAULT_LOW_BASE_WARN, DEFAULT_SUPPRESSION } from '@/lib/crosstab/base-thresholds';

/** Workspace settings. Lifted out of the `useState` initialiser so the tests can walk it. */
export interface CrosstabSettings {
    railOpen: boolean;
    railTab: string;
    showPct: boolean;
    showCounts: boolean;
    showBase: boolean;
    showIndex: boolean;
    sigStyle: string;
    sigScope: 'group' | 'all' | 'total' | 'reference';
    /** M4S-5777 (X74): the correction for many comparisons. Off ('none') by default. */
    sigCorrection: SigCorrection;
    heatmap: boolean;
    density: string;
    weighted: boolean;
    /**
     * M4S-5777 (X90): show the unweighted figures beside the weighted ones (Total and
     * "Total (weighted)" on screen, an unweighted set in every file). Record state, saved in the
     * weighting block (`ai_layout.state.weighting.showUnweighted`), and a compute input.
     */
    weightedShowUnweighted?: boolean;
    confidence: number;
    confidence2: number | null;
    showMean: boolean;
    showMedian: boolean;
    /** M4S-5777 (X77): standard deviation and standard error rows wherever a Mean is drawn. */
    showStdDev: boolean;
    showStdErr: boolean;
    /** M4S-5777 (X81): a Sigma (column total) row under each table's answers. */
    showSigma: boolean;
    /** M4S-5777 (X79): the single Top Box, crosstab-wide (Studio's picks), beside Top 2. */
    showTop1Box: boolean;
    /**
     * M4S-5777 (X72): the reference (control) columns by column id, read in the `reference` test
     * scope: each banner group holding one tests its other columns against it and shows their lift.
     */
    referenceCols: string[];
    /**
     * M4S-5777: the statistics rules the crosstab follows (`StatRules`, stat-settings.ts): 2 is
     * today's, 1 a crosstab saved before them, kept until one of its statistics is changed.
     */
    statRules: 1 | 2;
    showTopBox: boolean;
    showBottomBox: boolean;
    showNPS: boolean;
    hideTotal: boolean;
    hideOther: boolean;
    lowBaseWarn: number;
    suppressionThreshold: number;
    suppressionEnabled: boolean;
    filterToAllSurveys: boolean;
    bannerRenames: Record<string, string>;
    daniOpen: boolean;
    daniInline: boolean;
    view: string;
    theme: string;
    decimalPlaces: 0 | 1 | 2;
    /** R-A5: statistical test type. 'auto' = z for props, t for means (default). */
    statTest: 'auto' | 'z' | 'chi2';
    /** Per-question Top 2 / Bottom 2 / NPS choices and Reverse scale, keyed by question ID (qid). */
    perQuestionT2B2?: BoxChoiceMap;
    /**
     * Per-question matrix orientation keyed by UPPER-CASED qid: true = By Column, false = By Row,
     * for every matrix type (M4S-5618). Holds only differences from `matrixByColumn`.
     *
     * A setting, not loose React state: it feeds compute, so it marks the table dirty and it rides
     * `persistSettings` to localStorage and the POST body to the record (DoD item 4 criterion 5).
     */
    perQuestionMatrixTranspose?: Record<string, boolean>;
    /**
     * Crosstab-wide matrix orientation: true = By Column. Held by the record
     * (`ai_layout.state`) like the per-question map, not by the browser.
     */
    matrixByColumn?: boolean;
    /**
     * Option edits on matrix questions, keyed by UPPER-CASED qid (M4S-5618): statements and answer
     * choices renamed, hidden or reordered, and answer choices merged into nets, by survey code.
     * Held by the record (`ai_layout.state`) like the orientation, and a compute input like it, so
     * a Save in the Edit Row Variable dialog waits for Run. Flat `rowOverrides` no longer apply to
     * matrix questions.
     */
    matrixEdits?: MatrixEditsMap;
    /**
     * M4S-5777 (X59): matrices in the banner follow their orientation and option edits, as the
     * matrix's tables do (`matrix-banner.ts`). Held by the record. True on a new crosstab; a record
     * saved before X59 reads false, and draws its banner matrices By Row as surveyed (and says so)
     * until the first matrix edit, which sets it (`withMatrixBannerViews`). Never set back to false.
     */
    matrixBannerViews?: boolean;
    /**
     * M4S-5777 (K3): answers of ordinary questions renamed, hidden or moved, keyed by UPPER-CASED
     * qid and naming answers by survey code (`src/lib/crosstab/answer-edits.ts`). Held by the record
     * like `matrixEdits`, and a compute input like it, so an edit waits for Run. A browser's
     * `rowOverrides` is read only for a question this holds nothing for.
     */
    answerEdits?: AnswerEditsMap;
    /**
     * M4S-5777 X41: the question, survey or variable the crosstab can be flipped through, one tab
     * per answer after Total, or null. Held by the record (`ai_layout.state.filterTabs`) so everyone
     * sees the same tabs and the one-sheet-per-answer export has them. Not a compute input: a tab is
     * a view of the last Run's table cut to one answer (docs/pdlc/crosstabs-filters.md §4.3).
     */
    filterTabs?: FilterTabs | null;
    /**
     * M4S-5777 (X105): the export preset this crosstab was last exported with, or null for "the one
     * assigned to its account and study type" (`resolveExportPreset`). Held by the record
     * (`ai_layout.state.exportPreset`) so whoever exports it next gets the same deliverable. Not a
     * compute input: a preset changes formatting and names only.
     */
    exportPreset?: string | null;
    /**
     * M4S-5777 (X93, BT-1929): the Total column shows its count on every row even with Counts off.
     * A display preference (browser, and the person's, X102). On by default.
     */
    totalCounts: boolean;
    /**
     * M4S-5777 (X95): question IDs on titles, on screen, on charts and in the client-facing files
     * (Excel, PDF, PowerPoint). A display preference; the Export dialog can override it per file.
     */
    showQids: boolean;
    /**
     * M4S-5777 (X101): hide answers nobody chose, crosstab-wide. Record content (it changes what is
     * delivered) and a compute input, so it waits for Run. Saved in `ai_layout.state.emptyAnswers`
     * (`src/lib/crosstab/empty-answers.ts`).
     */
    hideEmptyAnswers?: boolean;
    /** M4S-5777 (X101): per question (upper-cased qid), true hides and false keeps, over the switch. */
    perQuestionHideEmpty?: Record<string, boolean>;
    [key: string]: unknown;
}

export const DEFAULT_SETTINGS: CrosstabSettings = {
    railOpen: true,
    railTab: 'build',
    showPct: true,
    showCounts: false,
    showBase: true,
    showIndex: false,
    sigStyle: 'letters',
    sigScope: 'group',
    sigCorrection: 'none',
    heatmap: false,
    density: 'comfortable',
    weighted: false,
    weightedShowUnweighted: false,
    confidence: 95,
    confidence2: null,
    showMean: false,
    showMedian: false,
    showStdDev: false,
    showStdErr: false,
    showSigma: false,
    showTop1Box: false,
    referenceCols: [],
    statRules: 2,
    showTopBox: false,
    showBottomBox: false,
    showNPS: false,
    hideTotal: false,
    // Catch-all ":__other" complements are hidden by default, and the choice is session-only —
    // see SESSION_ONLY_SETTINGS. Requested by the OOH team, 2026-09-21.
    hideOther: true,
    lowBaseWarn: DEFAULT_LOW_BASE_WARN,
    suppressionThreshold: DEFAULT_SUPPRESSION,
    suppressionEnabled: false,
    filterToAllSurveys: false,
    bannerRenames: {},
    daniOpen: false,
    daniInline: false,
    view: 'table',
    theme: 'light',
    decimalPlaces: 0,
    statTest: 'auto',
    perQuestionT2B2: {},
    perQuestionMatrixTranspose: {},
    matrixByColumn: false,
    matrixEdits: {},
    matrixBannerViews: true,
    answerEdits: {},
    filterTabs: null,
    exportPreset: null,
    totalCounts: true,
    showQids: true,
    hideEmptyAnswers: false,
    perQuestionHideEmpty: {},
};

/**
 * Settings that are deliberately NOT remembered between sessions. They are dropped on the way into
 * localStorage AND on the way back out, so every load starts from `DEFAULT_SETTINGS` for these
 * keys no matter what an older session left behind.
 *
 * `daniOpen` / `daniInline` are panel state — where the DANI chat sits is a property of the
 * moment, not of the crosstab.
 *
 * `hideOther` is the ":__other" catch-all column toggle. It is stored per browser per project and
 * never reaches the `ai_layout` record, so persisting it meant the same table showed different
 * columns to different people — and the columns in question are stripped from every export
 * unconditionally (`stripOtherCols`) and carry no significance letter (BT-2016). Making it
 * session-only turns it into "let me look at the residual", which is what it is for: turn it on,
 * look, and the next person to open the project still gets the clean table.
 */
export const SESSION_ONLY_SETTINGS = ['daniOpen', 'daniInline', 'hideOther'] as const;

/** A copy of `settings` with every {@link SESSION_ONLY_SETTINGS} key removed. */
export function stripSessionOnly<T extends object>(settings: T): Partial<T> {
    const out = { ...settings } as Record<string, unknown>;
    for (const key of SESSION_ONLY_SETTINGS) delete out[key];
    return out as Partial<T>;
}

/**
 * Inputs that feed compute but deliberately live OUTSIDE `committedState`: changing one recomputes
 * synchronously rather than enabling Run. The names are the ones the `computed` memo's dependency
 * array uses, and compute-inputs.test.ts scans the source to check they are all still in it.
 *
 * Only the survey names are left (they label the wave columns; no number moves). M4S-5777 X34
 * (decision E1) moved `liveFilters`, `weightScheme` and the row overrides into `committedState`:
 * they change numbers, so they wait for Run like every other edit.
 */
export const LIVE_COMPUTE_INPUTS = ['availableSurveys'] as const;

/**
 * Every setting `buildComputeOpts` reads. Changing one of these — and only one of these — must
 * make `isComputeDirty` true. compute-inputs.test.ts holds it to `buildComputeOpts`, and Cancel
 * reads it at runtime (`discardToRun`): these are the settings that wait for Run.
 */
export const COMPUTE_SETTING_KEYS = [
    'weighted',
    'weightedShowUnweighted',
    'confidence',
    'confidence2',
    'lowBaseWarn',
    'suppressionThreshold',
    'showMean',
    'showMedian',
    'showStdDev',
    'showStdErr',
    'showSigma',
    'showTop1Box',
    'referenceCols',
    'statRules',
    'showTopBox',
    'showBottomBox',
    'showNPS',
    'sigScope',
    'sigCorrection',
    'filterToAllSurveys',
    'statTest',
    'perQuestionT2B2',
    'perQuestionMatrixTranspose',
    'matrixByColumn',
    'matrixEdits',
    'matrixBannerViews',
    'answerEdits',
    // M4S-5777 (X101): which answers a table draws.
    'hideEmptyAnswers',
    'perQuestionHideEmpty',
] as const;

/**
 * The settings the crosstab record holds (`ai_layout.state`), as opposed to the browser.
 *
 * ### The four lists (M4S-5613)
 *
 * Every `DEFAULT_SETTINGS` key is filed in exactly one of four LITERAL lists, and a test walks the
 * defaults to check it: {@link RECORD_SETTING_KEYS}, {@link SESSION_ONLY_SETTINGS},
 * {@link PENDING_M4S_5607_KEYS} and {@link BROWSER_ONLY_SETTING_KEYS}. Nothing derives one list
 * from the others, so a new setting fails that test until somebody files it on purpose. A new
 * crosstab must not inherit the record's settings from another crosstab's browser slot, and an
 * opened one must not show a browser value where the record has its own.
 *
 * **The filing rule.** A choice that is part of the crosstab's CONTENT (which rows, nets or summary
 * tables exist, and every Top 2 / Bottom 2 choice) is a record key, and takes the wiring recipe
 * `matrixEdits` took: the save payload and `layoutSignature`, `buildAiLayout` and
 * `withAiStateDefaults`, the route's POST, and a hydrate patch that always sets it. So is every
 * STATISTICS setting (M4S-5777 X14): the significance levels, the test and what it compares, the
 * markers, Mean and Median, decimals, the low-base thresholds and "completes only" decide the
 * letters and figures everyone sees and every export prints, so two people opening one crosstab
 * must see the same ones. They travel as one block, `ai_layout.state.statSettings`, whose one home
 * is `src/lib/crosstab/stat-settings.ts` (`STAT_SETTING_KEYS`). A DISPLAY preference (the rail, the
 * view, counts, density, the heatmap) is browser-only, and the person's own across browsers (X102,
 * `src/lib/crosstab/display-prefs.ts`).
 */
export const RECORD_SETTING_KEYS = [
    'bannerRenames',
    'perQuestionMatrixTranspose',
    'matrixByColumn',
    'matrixEdits',
    // M4S-5777 (X59): whether banner matrices follow their orientation and edits is content.
    'matrixBannerViews',
    // M4S-5607: every Top 2 / Bottom 2 / NPS choice that changes a table. The three crosstab-wide
    // switches and the per-question map travel together, because compute falls back to the switch
    // for any question without its own entry: a browser-held switch under a record-held exception
    // would give two viewers two tables.
    'showTopBox',
    'showBottomBox',
    'showNPS',
    'perQuestionT2B2',
    // BT-2084: whether the crosstab is weighted. Its scheme (`weightScheme`, a live input) is
    // record state too, saved beside it, so everyone who opens the crosstab sees the same numbers.
    'weighted',
    // M4S-5777 (X90): the unweighted figures beside the weighted ones, saved in the weighting block.
    'weightedShowUnweighted',
    // M4S-5777 X14: the statistics settings, saved together as `ai_layout.state.statSettings`
    // (`STAT_SETTING_KEYS` in src/lib/crosstab/stat-settings.ts names the same nineteen;
    // `sigCorrection` joined with X74, and the six after `filterToAllSurveys` with X72-X81).
    'confidence',
    'confidence2',
    'sigScope',
    'sigCorrection',
    'statTest',
    'sigStyle',
    'showMean',
    'showMedian',
    'decimalPlaces',
    'lowBaseWarn',
    'suppressionThreshold',
    'suppressionEnabled',
    'filterToAllSurveys',
    'showStdDev',
    'showStdErr',
    'showSigma',
    'showTop1Box',
    'referenceCols',
    'statRules',
    // M4S-5777 (K3): renamed, hidden and moved answers of ordinary questions are content.
    'answerEdits',
    // M4S-5777 X41: the filter tabs are content: which tabs and which sheets the crosstab has.
    'filterTabs',
    // M4S-5777 (X105): the deliverable's export preset is the crosstab's, not the browser's.
    'exportPreset',
    // M4S-5777 (X101): hidden empty answers are content: they change what is delivered. Saved
    // together as `ai_layout.state.emptyAnswers` (src/lib/crosstab/empty-answers.ts).
    'hideEmptyAnswers',
    'perQuestionHideEmpty',
] as const;

export type RecordSettingKey = (typeof RECORD_SETTING_KEYS)[number];

/**
 * Empty since M4S-5607 C5. It held `showTopBox`, `showBottomBox`, `showNPS` and `perQuestionT2B2`
 * while they were record state that the browser still remembered, until C5 wired them through the
 * record and moved all four into {@link RECORD_SETTING_KEYS}. The list stays as the place a setting
 * waits when it is filed before its wiring lands, and the partition test asserts it is empty, so a
 * key left in it fails the build instead of quietly staying a browser value.
 */
export const PENDING_M4S_5607_KEYS = [] as const;

/**
 * Display preferences the browser remembers, per crosstab, in that crosstab's own
 * `ct:layout:<record key>` entry (M4S-5613 commit 3). They never reach the record. The statistics
 * settings left this list for the record's in M4S-5777 X14; the ones a person keeps across
 * browsers are `DISPLAY_PREF_KEYS` (src/lib/crosstab/display-prefs.ts), a subset of this list.
 */
export const BROWSER_ONLY_SETTING_KEYS = [
    'railOpen',
    'railTab',
    'showPct',
    'showCounts',
    'showBase',
    'showIndex',
    'heatmap',
    'density',
    'hideTotal',
    'view',
    'theme',
    // M4S-5777 (X93, X95): display preferences, the person's too (X102).
    'totalCounts',
    'showQids',
] as const;

const BROWSER_STORED_KEYS: ReadonlySet<string> = new Set<string>([
    ...BROWSER_ONLY_SETTING_KEYS,
    ...PENDING_M4S_5607_KEYS,
]);

/**
 * A copy of `settings` holding only what the browser is allowed to remember for a crosstab: the
 * browser-only keys and the pending ones. Record keys, session-only keys and anything unknown are
 * left out, so a new crosstab or an opened record can take its settings from a browser slot
 * without taking the record's from somebody else's. (DANI's `layout.settings` can inject arbitrary
 * keys, so "unknown" is not hypothetical.)
 */
export function pickBrowserOnly<T extends object>(settings: T): Partial<T> {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(settings)) {
        if (BROWSER_STORED_KEYS.has(key)) out[key] = value;
    }
    return out as Partial<T>;
}

/** The compute inputs that are live by design — passed identically to both sides of a dirty check. */
export interface LiveComputeInputs {
    liveFilters?: LiveFilter[];
    /** Already mapped to proportions; `settings.weighted` gates whether they are applied. */
    weightTargets?: WeightTarget[];
    /** BT-2084: the rest of the weight scheme (`weightComputeInputs`), gated with the targets. */
    weightMethod?: ComputeOpts['weightMethod'];
    weightScope?: ComputeOpts['weightScope'];
    weightMatch?: ComputeOpts['weightMatch'];
    weightNote?: string;
    /** M4S-5777: the scheme was saved under today's condition rules (`rules: 2`). */
    weightRules?: ComputeOpts['weightRules'];
    /** M4S-5777 (X83, X88, X91): the weight variable, the limits and the disclosure. */
    weightVariable?: ComputeOpts['weightVariable'];
    weightLimits?: ComputeOpts['weightLimits'];
    weightDisclosure?: ComputeOpts['weightDisclosure'];
    surveyNames?: Record<string, string>;
    rowOverrides?: Record<string, CrosstabQuestionOptionDto[]>;
}

const nonEmpty = <T extends object>(v: T | undefined): T | undefined =>
    v && Object.keys(v).length ? v : undefined;

/** The one place the compute options object is built. */
export function buildComputeOpts(
    settings: CrosstabSettings,
    live: LiveComputeInputs = {},
): ComputeOpts {
    // The whole scheme, or none of it: the method was dropped here once (7b8ab4f09, BT-2084).
    // M4S-5777 (X83): a scheme that reads weights from a variable has no targets.
    const w =
        settings.weighted &&
        (live.weightTargets?.length || (live.weightMethod === 'variable' && live.weightVariable))
            ? live
            : undefined;
    return {
        weighted: settings.weighted,
        confidence: settings.confidence,
        confidence2: settings.confidence2 ?? undefined,
        lowBaseWarn: settings.lowBaseWarn,
        suppressionThreshold: settings.suppressionThreshold,
        showMean: settings.showMean,
        showMedian: settings.showMedian,
        // M4S-5777 (X72-X81): each absent at its default, so a crosstab saved before them builds
        // the options it always built and never reads dirty.
        showStdDev: settings.showStdDev || undefined,
        showStdErr: settings.showStdErr || undefined,
        showSigma: settings.showSigma || undefined,
        showTop1Box: settings.showTop1Box || undefined,
        // Read by compute only in the reference scope; passed whenever there are any, so a change
        // to them always waits for Run.
        referenceCols: settings.referenceCols?.length ? settings.referenceCols : undefined,
        // Today's rules are the default; only a crosstab saved before them says so.
        statRules: settings.statRules === undefined || settings.statRules === 2 ? undefined : 1,
        showTopBox: settings.showTopBox,
        showBottomBox: settings.showBottomBox,
        showNPS: settings.showNPS,
        sigScope: settings.sigScope,
        // Absent when off, so a crosstab without the key builds the options it always built. Passed
        // through otherwise: compute reads it through `sigRuleOf`, and a parse here could hide a
        // change from the dirty check.
        sigCorrection:
            settings.sigCorrection && settings.sigCorrection !== 'none'
                ? settings.sigCorrection
                : undefined,
        liveFilters: live.liveFilters?.length ? live.liveFilters : undefined,
        filterToAllSurveys: settings.filterToAllSurveys || undefined,
        surveyNames: nonEmpty(live.surveyNames),
        weightTargets: w?.weightTargets,
        weightMethod: w?.weightMethod,
        weightScope: w?.weightScope,
        weightMatch: w?.weightMatch,
        weightNote: w?.weightNote,
        weightRules: w?.weightRules,
        weightVariable: w?.weightVariable,
        weightLimits: w?.weightLimits,
        weightDisclosure: w?.weightDisclosure,
        // M4S-5777 (X90): read by the compute only when it weights. `|| undefined` so false and
        // absent build the same object.
        showUnweighted: settings.weightedShowUnweighted || undefined,
        statTest: (settings.statTest as 'auto' | 'z' | 'chi2') ?? 'auto',
        perQuestionT2B2: nonEmpty(settings.perQuestionT2B2),
        rowOverrides: nonEmpty(live.rowOverrides),
        perQuestionMatrixTranspose: nonEmpty(settings.perQuestionMatrixTranspose),
        // `|| undefined` so false and absent build the same object and a default never reads dirty.
        matrixByColumn: settings.matrixByColumn || undefined,
        // Passed through unvalidated: the reads that bring it into settings parse it (the record,
        // the browser slot, the hydrate), and the compute resolves it against each question. A
        // parse here as well could hide a change from the dirty check.
        matrixEdits: nonEmpty(settings.matrixEdits),
        // M4S-5777 (X59): `|| undefined`, so false and absent build the same object.
        matrixBannerViews: settings.matrixBannerViews || undefined,
        // M4S-5777: passed through unvalidated, as `matrixEdits` is; the reads parse it.
        answerEdits: nonEmpty(settings.answerEdits),
        // M4S-5777 (X101): `|| undefined` and `nonEmpty`, so a crosstab without them builds the
        // options it always built and never reads dirty.
        hideEmptyAnswers: settings.hideEmptyAnswers || undefined,
        perQuestionHideEmpty: nonEmpty(settings.perQuestionHideEmpty),
    };
}

/** The configuration a compute is a function of. */
export interface ComputeState {
    activeRows: string[];
    activeBanner: string[];
    customVariables: unknown[];
    activeSurveyIds: string[];
    settings: CrosstabSettings;
    /**
     * M4S-5777 X34: the inputs beyond the settings that wait for Run (filters, the weight scheme,
     * row overrides; `runInputs` in src/lib/crosstab/pending-run.ts builds them). Absent is none.
     */
    inputs?: LiveComputeInputs;
}

/**
 * Is the table out of date — i.e. should Run be enabled?
 *
 * Each side's options are built from its own settings and its own `inputs`, so a filter, a weight
 * scheme (while weighting is on) or a row override changed since the last Run lights Run up. The
 * survey names are dropped from both sides: they label columns and change no number.
 */
export function isComputeDirty(live: ComputeState, committed: ComputeState | null): boolean {
    if (!committed) return false;
    if (
        JSON.stringify(live.activeRows) !== JSON.stringify(committed.activeRows) ||
        JSON.stringify(live.activeBanner) !== JSON.stringify(committed.activeBanner) ||
        JSON.stringify(live.customVariables) !== JSON.stringify(committed.customVariables) ||
        JSON.stringify(live.activeSurveyIds) !== JSON.stringify(committed.activeSurveyIds)
    )
        return true;
    const probe = (state: ComputeState): LiveComputeInputs => ({
        ...state.inputs,
        surveyNames: undefined,
    });
    return (
        JSON.stringify(buildComputeOpts(live.settings, probe(live))) !==
        JSON.stringify(buildComputeOpts(committed.settings, probe(committed)))
    );
}
